import { Slider } from "@/components/ui/slider";
import { Flame, Droplets, Thermometer, Snowflake } from "@/lib/icons";

interface LabControlsProps {
  concentration: number;
  onConcentrationChange: (n: number) => void;
  temperature: number;
  onTemperatureChange: (n: number) => void;
  /** Disable interaction during animations. */
  disabled?: boolean;
}

const TEMP_MIN = -20;
const TEMP_MAX = 1000;

/** Compact dilute / concentrated + hot-plate controls for the mixing station. */
export default function LabControls({
  concentration,
  onConcentrationChange,
  temperature,
  onTemperatureChange,
  disabled,
}: LabControlsProps) {
  const concPct = Math.round(concentration * 100);
  const isHot = temperature > 100;
  const isCold = temperature <= 5;

  return (
    <div className="w-full max-w-md grid grid-cols-1 sm:grid-cols-2 gap-3">
      {/* Concentration */}
      <div className="rounded-lg border border-border bg-card p-3">
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="lab-concentration" className="flex items-center gap-1.5 text-xs font-medium text-foreground">
            <Droplets size={12} className="text-primary" aria-hidden="true" />
            Concentration
          </label>
          <span className="text-[11px] font-mono text-muted-foreground tabular-nums">{concPct}%</span>
        </div>
        <Slider
          id="lab-concentration"
          min={10}
          max={100}
          step={5}
          value={[concPct]}
          onValueChange={(v) => onConcentrationChange((v[0] ?? 50) / 100)}
          disabled={disabled}
          aria-label="Solution concentration"
        />
        <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
          <span>Dilute</span>
          <span>Concentrated</span>
        </div>
      </div>

      {/* Temperature */}
      <div className="rounded-lg border border-border bg-card p-3">
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="lab-temperature" className="flex items-center gap-1.5 text-xs font-medium text-foreground">
            {isHot ? (
              <Flame size={12} className="text-orange-500" aria-hidden="true" />
            ) : isCold ? (
              <Snowflake size={12} className="text-sky-400" aria-hidden="true" />
            ) : (
              <Thermometer size={12} className="text-primary" aria-hidden="true" />
            )}
            Hot plate
          </label>
          <span
            className={`text-[11px] font-mono tabular-nums ${
              isHot ? "text-orange-500 font-semibold" : isCold ? "text-sky-400 font-semibold" : "text-muted-foreground"
            }`}
          >
            {temperature} °C
          </span>
        </div>
        <Slider
          id="lab-temperature"
          min={TEMP_MIN}
          max={TEMP_MAX}
          step={25}
          value={[temperature]}
          onValueChange={(v) => onTemperatureChange(v[0] ?? TEMP_MIN)}
          disabled={disabled}
          aria-label="Hot-plate temperature in degrees Celsius"
        />
        <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
          <span>−20 °C (ice)</span>
          <span>1000 °C</span>
        </div>
      </div>
    </div>
  );
}